import React from "react";
import AutoDeleteOutlinedIcon from "@mui/icons-material/AutoDeleteOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NorthEastOutlinedIcon from "@mui/icons-material/NorthEastOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import KeyOffOutlinedIcon from "@mui/icons-material/KeyOffOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { StatusChip } from "components/common/StatusChip";
import { LogDays } from "@/components/main/governance/LogDays";
import type { AuditNames, GovernanceAuditEntry } from "@/lib/api/organizations";
import { Principal } from "@/components/main/governance/Principal";
import {
  phraseFor,
  type AuditKind,
  type AuditPart,
} from "@/components/main/governance/auditPhrasing";

interface Props {
  entries: GovernanceAuditEntry[];
  names: AuditNames;
  /// Suffix each row with its space: on the org log "it" alone would not say
  /// which space was renamed; on a space's own page every row is about it.
  showSpace?: boolean;
  emptyLabel?: string;
}

const MARKS: Record<AuditKind, React.ElementType> = {
  created: AddOutlinedIcon,
  renamed: DriveFileRenameOutlineOutlinedIcon,
  sealed: LockOutlinedIcon,
  unsealed: LockOpenOutlinedIcon,
  granted: KeyOutlinedIcon,
  revoked: KeyOffOutlinedIcon,
  invited: EmailOutlinedIcon,
  joined: LoginOutlinedIcon,
  left: LogoutOutlinedIcon,
  removed: PersonRemoveOutlinedIcon,
  role: NorthEastOutlinedIcon,
  doomed: AutoDeleteOutlinedIcon,
  restored: SettingsBackupRestoreIcon,
  moved: DriveFileMoveOutlinedIcon,
  deleted: DeleteOutlineOutlinedIcon,
  billing: CreditCardOutlinedIcon,
  limit: TuneOutlinedIcon,
  reset: RestartAltOutlinedIcon,
};

const Verb: React.FC<{ parts: AuditPart[] }> = ({ parts }) => (
  <>
    {parts.map((part, i) =>
      typeof part === "string" ? (
        part
      ) : (
        <span key={i} className="text-fg-regular">
          {part.name}
        </span>
      ),
    )}
  </>
);

/// Only the acts that take something away are inked — everything else would
/// turn a log into a colour chart.
const LOUD: AuditKind[] = ["doomed", "deleted", "removed", "revoked"];

export const GovernanceAuditList: React.FC<Props> = ({
  entries,
  names,
  showSpace = false,
  emptyLabel = "No governance events recorded.",
}) => {
  if (entries.length === 0) {
    return (
      <span className="px-2 text-fg-muted-regular-small">{emptyLabel}</span>
    );
  }

  return (
    <LogDays
      entries={entries}
      at={(e) => e.created_at}
      line={(entry) => {
        const phrase = phraseFor(entry, names);
        const spaceName =
          showSpace && entry.space_id ? names.spaces[entry.space_id] : null;
        const kind = phrase.kind;
        const Mark = kind ? MARKS[kind] : null;
        return {
          key: entry.id,
          mark: Mark && (
            <Mark
              sx={{ fontSize: 12 }}
              className={
                kind && LOUD.includes(kind) ? "text-warning" : "text-fg-muted"
              }
            />
          ),
          body: (
            <>
              <Principal
                id={entry.actor}
                name={entry.actor_name}
                email={entry.actor_email}
              />{" "}
              {entry.action.startsWith("platform.") &&
                entry.actor !== "Mycorr Admin" && (
                  <>
                    <StatusChip tone="accent">mycorr</StatusChip>{" "}
                  </>
                )}
              <Verb parts={phrase.verb} />
              {phrase.subject && entry.subject ? (
                <>
                  {" "}
                  <Principal
                    id={entry.subject}
                    name={entry.subject_name}
                    email={entry.subject_email}
                  />
                </>
              ) : null}
              {phrase.tail ? ` ${phrase.tail}` : ""}
              {spaceName && !phrase.namesSpace ? (
                <span className="text-fg-muted"> · {spaceName}</span>
              ) : null}
              {typeof entry.detail?.["reason"] === "string" && (
                <span className="block">{entry.detail["reason"]}</span>
              )}
            </>
          ),
        };
      }}
    />
  );
};
